<template>
  <div v-if="visivel" class="na-launcher">
    <v-tooltip left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          fab
          color="error"
          class="na-fab"
          elevation="6"
          aria-label="Iniciar novo atendimento"
          v-bind="attrs"
          v-on="on"
          @click="abrir"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <span>Novo atendimento</span>
    </v-tooltip>

    <v-dialog
      v-model="aberto"
      max-width="760"
      scrollable
      transition="dialog-bottom-transition"
      content-class="na-dialog"
    >
      <v-card class="na-card">
        <header class="na-header">
          <div class="na-header__icon">
            <v-icon color="white">mdi-stethoscope</v-icon>
          </div>
          <div class="na-header__text">
            <span class="na-title">Novo atendimento</span>
            <span class="na-subtitle">Escolha o paciente para começar a consulta</span>
          </div>
          <v-spacer />
          <v-btn class="close-btn" icon dark aria-label="Fechar" @click="aberto = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <div class="na-toolbar">
          <!--
            salvia-778: o passo da clinica SO aparece quando ha escolha real.

            Medico solo nao tem clinica nenhuma (os pacientes dele vem por
            `patients-by-doctor`), e quem tem uma so nao tem o que decidir. Em
            ambos os casos, perguntar seria um passo que so existe para ser
            respondido sempre igual.
          -->
          <!--
            SALVIA-945: escolha de clínicas numa linha só, sem a paginação dos
            chips — digita-se o nome, marcam-se várias (ou todas) e a lista de
            pacientes acompanha. Com muitas marcadas, mostra a primeira e "+N".
          -->
          <v-autocomplete
            v-if="clinicas.length > 1"
            v-model="clinicaIds"
            :items="clinicas"
            item-text="name"
            item-value="id"
            multiple
            dense
            solo
            flat
            hide-details
            prepend-inner-icon="mdi-hospital-building"
            placeholder="Escolha a clínica"
            no-data-text="Nenhuma clínica com esse nome"
            class="na-clinicas"
            :menu-props="{ offsetY: true, maxHeight: 280 }"
            @change="carregarPacientes"
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @mousedown.prevent @click="alternarTodasClinicas">
                <v-list-item-action>
                  <v-icon :color="todasClinicas ? 'error' : ''">{{ iconeTodasClinicas }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Todas as clínicas</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-1" />
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0" class="na-clinicas__resumo">
                {{ todasClinicas ? "Todas as clínicas" : item.name }}
              </span>
              <span
                v-else-if="index === 1 && !todasClinicas"
                class="na-clinicas__mais"
              >+{{ clinicaIds.length - 1 }}</span>
            </template>
          </v-autocomplete>

          <div class="na-busca-linha">
            <v-text-field
              ref="busca"
              v-model="busca"
              placeholder="Buscar paciente pelo nome"
              prepend-inner-icon="mdi-magnify"
              solo
              flat
              clearable
              hide-details
              class="na-busca"
              @keydown.enter="iniciarPrimeiro"
            >
              <template v-slot:append>
                <span v-if="!carregando && pacientes.length" class="na-contagem">
                  {{ filtrados.length }}
                  {{ filtrados.length === 1 ? "paciente" : "pacientes" }}
                </span>
              </template>
            </v-text-field>
            <!-- SALVIA-945: paciente que ainda não existe — cadastro rápido por cima
                 desta tela; ao salvar, ele aparece aqui mesmo para iniciar. -->
            <v-btn
              outlined
              color="error"
              height="48"
              class="na-novo-paciente"
              data-tour="na-novo-paciente"
              @click="abrirCadastroRapido"
            >
              <v-icon left>mdi-account-plus</v-icon>
              <span class="na-novo-paciente__texto">Cadastrar novo paciente</span>
            </v-btn>
          </div>
        </div>

        <v-card-text class="ds-dialog-body na-corpo">
          <div v-if="carregando" class="na-grade">
            <div v-for="n in 6" :key="n" class="na-esqueleto">
              <span class="na-esqueleto__avatar" />
              <span class="na-esqueleto__linhas">
                <span />
                <span />
              </span>
            </div>
          </div>

          <div v-else-if="!pacientes.length" class="na-vazio">
            <v-icon size="44" class="na-vazio__icone">mdi-account-off-outline</v-icon>
            <p>Nenhum paciente encontrado.</p>
            <v-btn small text color="error" class="mt-2" @click="abrirCadastroRapido">
              <v-icon small left>mdi-account-plus</v-icon>
              Cadastrar novo paciente
            </v-btn>
          </div>

          <div v-else-if="!filtrados.length" class="na-vazio">
            <v-icon size="44" class="na-vazio__icone">mdi-account-search-outline</v-icon>
            <p>Nenhum paciente corresponde a “{{ busca }}”.</p>
            <v-btn small text color="error" class="mt-2" @click="abrirCadastroRapido">
              <v-icon small left>mdi-account-plus</v-icon>
              Cadastrar “{{ busca }}”
            </v-btn>
          </div>

          <template v-else>
            <!--
              Quem ja tem consulta hoje vem primeiro, em ordem de horario. NAO
              impede nada: o motivo da consulta e preenchido a partir da agenda
              de qualquer forma (salvia-839), entao iniciar fora dele nao
              produz atendimento solto.
            -->
            <section v-if="deHoje.length" class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                  <span>Com consulta hoje</span>
                </div>
              </div>
              <transition-group name="na-item" tag="div" class="na-grade">
                <button
                  v-for="p in deHoje"
                  :key="p.id"
                  type="button"
                  class="na-paciente na-paciente--hoje"
                  :class="{ 'na-paciente--novo': p.id === destacadoId }"
                  :data-paciente-id="p.id"
                  @click="iniciar(p)"
                >
                  <v-avatar size="40" class="na-paciente__avatar">
                    <v-img :src="p.profile_image || avatarPadrao" />
                  </v-avatar>
                  <span class="na-paciente__info">
                    <span class="na-paciente__nome">
                      <span
                        v-for="(parte, i) in partesDoNome(p.name)"
                        :key="i"
                        :class="{ 'na-destaque': parte.destaque }"
                        >{{ parte.texto }}</span
                      >
                    </span>
                    <span class="na-paciente__hora">
                      <v-icon x-small>mdi-clock-outline</v-icon>
                      às {{ temConsultaHoje(p.id) }}
                    </span>
                  </span>
                  <v-icon small class="na-paciente__seta">mdi-play-circle</v-icon>
                </button>
              </transition-group>
            </section>

            <section v-if="demais.length" class="ds-form-section">
              <div v-if="deHoje.length" class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-account-multiple-outline</v-icon>
                  <span>Demais pacientes</span>
                </div>
              </div>
              <transition-group name="na-item" tag="div" class="na-grade">
                <button
                  v-for="p in demais"
                  :key="p.id"
                  type="button"
                  class="na-paciente"
                  :class="{ 'na-paciente--novo': p.id === destacadoId }"
                  :data-paciente-id="p.id"
                  @click="iniciar(p)"
                >
                  <v-avatar size="40" class="na-paciente__avatar">
                    <v-img :src="p.profile_image || avatarPadrao" />
                  </v-avatar>
                  <span class="na-paciente__info">
                    <span class="na-paciente__nome">
                      <span
                        v-for="(parte, i) in partesDoNome(p.name)"
                        :key="i"
                        :class="{ 'na-destaque': parte.destaque }"
                        >{{ parte.texto }}</span
                      >
                    </span>
                    <span v-if="p.email" class="na-paciente__meta">{{ p.email }}</span>
                  </span>
                  <v-icon small class="na-paciente__seta">mdi-arrow-right</v-icon>
                </button>
              </transition-group>
            </section>
          </template>
        </v-card-text>

        <footer v-if="!carregando && filtrados.length" class="na-rodape">
          <v-icon x-small class="mr-1">mdi-keyboard-return</v-icon>
          Enter inicia com o primeiro da lista
        </footer>
      </v-card>
    </v-dialog>

    <QuickPatientDialog
      v-model="cadastroRapido"
      :clinics="clinicas"
      :default-clinic-ids="clinicaIds"
      :initial-name="busca || ''"
      @saved="aoCadastrarRapido"
    />
  </div>
</template>

<script>
import http from "@/services/http";
import { getMyClinics, getPatientsByClinics } from "@/services/clinicService";
import { formatTime, userTimeZone } from "@/utils/datetime";
import QuickPatientDialog from "@/features/patients/atendimento/QuickPatientDialog.vue";
import { onPatientListChanged } from "@/services/patientListBus";

/**
 * Atalho global de "Novo atendimento" — salvia-778, ata de 13/08.
 *
 * O pedido: iniciar uma consulta de QUALQUER tela, sem ter que entrar no
 * cadastro do paciente antes; o sistema pergunta quem e o paciente na hora.
 *
 * Vive no dock flutuante (App.vue), entao esta em todas as telas por
 * construcao — e nao so na agenda e na lista de pacientes, que era o minimo
 * que a ata pedia.
 *
 * O destino e o mesmo do botao do card e do detalhe do compromisso
 * (salvia-837): `?atendimento=novo` no prontuario, que o dashboard le e abre
 * o modal na chegada.
 */
export default {
  name: "NovoAtendimentoLauncher",

  components: { QuickPatientDialog },

  data() {
    return {
      aberto: false,
      // A lista mudou em outra tela (paciente ocultado, reexibido, cadastrado):
      // a próxima abertura recarrega antes de mostrar.
      listaDesatualizada: false,
      carregando: false,
      clinicas: [],
      // SALVIA-945: várias clínicas ao mesmo tempo (antes, uma só).
      clinicaIds: [],
      // Paciente recém-cadastrado: a borda pisca duas vezes para achá-lo.
      destacadoId: null,
      pacientes: [],
      busca: "",
      // SALVIA-945: cadastro rápido aberto por cima desta tela.
      cadastroRapido: false,
      consultasDeHoje: {},
      avatarPadrao: require("@/assets/images/profile_default_image.png"),
    };
  },

  computed: {
    userType() {
      return this.$store.getters["auth/userType"];
    },

    userId() {
      return this.$store.getters["auth/userId"];
    },

    todasClinicas() {
      return this.clinicas.length > 0 && this.clinicaIds.length === this.clinicas.length;
    },
    iconeTodasClinicas() {
      if (this.todasClinicas) return "mdi-checkbox-marked";
      return this.clinicaIds.length ? "mdi-minus-box" : "mdi-checkbox-blank-outline";
    },
    /** O atendimento e do MEDICO. Assistente e paciente nao veem o atalho. */
    visivel() {
      return ["doctor_solo", "doctor_clinic"].includes(this.userType);
    },

    /**
     * Filtragem NO NAVEGADOR, sobre a lista ja carregada.
     *
     * Nao e economia de endpoint: `users.name` esta em claro hoje, mas ficou
     * registrado que um dia pode ser cifrado. Busca feita em SQL quebraria
     * naquele dia, em silencio — `WHERE name LIKE` nao casa com envelope.
     * Aqui continua funcionando.
     */
    filtrados() {
      const termo = (this.busca || "").trim().toLowerCase();

      if (!termo) return this.pacientes;

      return this.pacientes.filter((p) =>
        String(p.name || "").toLowerCase().includes(termo),
      );
    },

    /** Com consulta hoje, em ordem de horario ("08:30" < "14:00" como texto). */
    deHoje() {
      return this.filtrados
        .filter((p) => this.temConsultaHoje(p.id))
        .sort((a, b) =>
          this.temConsultaHoje(a.id).localeCompare(this.temConsultaHoje(b.id)),
        );
    },

    demais() {
      return this.filtrados.filter((p) => !this.temConsultaHoje(p.id));
    },

    /** A ordem em que aparecem na tela — e a que o Enter respeita. */
    ordenados() {
      return [...this.deHoje, ...this.demais];
    },
  },

  mounted() {
    this._pedidoLista = 0;
    this._offPatientList = onPatientListChanged(() => {
      this.listaDesatualizada = true;
    });
  },

  beforeDestroy() {
    this._offPatientList?.();
  },

  methods: {
    async abrir() {
      this.aberto = true;
      this.busca = "";

      // Foco direto na busca: quem abre o atalho ja sabe quem vai atender.
      this.$nextTick(() => setTimeout(() => this.$refs.busca?.focus(), 150));

      if (this.pacientes.length && !this.listaDesatualizada) {
        // Abre na hora com a lista que já tem e atualiza por trás: um paciente
        // pode ter mudado noutra aba ou por outra pessoa. A agenda do dia muda
        // o tempo todo (salvia-779: atendidos saem, encaixes entram).
        this.recarregarLista({ silencioso: true });
        this.carregarConsultasDeHoje();
        return;
      }

      // Mudou nesta aba (ocultou, reexibiu, cadastrou): recarrega ANTES de
      // mostrar — senão o paciente ocultado aparece por um instante.
      if (this.pacientes.length) {
        this.carregando = true;
        try {
          await Promise.all([this.recarregarLista(), this.carregarConsultasDeHoje()]);
        } finally {
          this.carregando = false;
        }
        return;
      }

      this.carregando = true;

      try {
        await Promise.all([this.carregarOrigem(), this.carregarConsultasDeHoje()]);
      } finally {
        this.carregando = false;
      }
    },

    /** De onde vem a lista depende do tipo de medico. */
    async carregarOrigem() {
      if (this.userType === "doctor_solo") {
        return this.carregarPacientesDoMedico();
      }

      this.clinicas = await getMyClinics();

      if (!this.clinicas.length) {
        // Medico de clinica sem clinica vinculada: cai no proprio vinculo, em
        // vez de mostrar lista vazia sem explicacao.
        return this.carregarPacientesDoMedico();
      }

      this.clinicaIds = [this.clinicas[0].id];

      return this.carregarPacientes();
    },

    /**
     * Pacientes das clínicas marcadas. O endpoint é por clínica: uma chamada
     * por clínica, em paralelo, e a lista junta sem repetir quem é atendido
     * em mais de uma.
     */
    async carregarPacientes({ silencioso = false } = {}) {
      // Silencioso = atualização por trás da lista já na tela: sem spinner e,
      // se falhar, a lista que estava fica.
      const pedido = ++this._pedidoLista;
      if (!silencioso) this.carregando = true;

      try {
        const listas = await Promise.all(
          // SALVIA-955: sem os pacientes que o médico ocultou da lista dele.
          this.clinicaIds.map((id) =>
            getPatientsByClinics(id, { visibility: "visible" }).catch(() => []),
          ),
        );
        // Trocou de clínica no meio do caminho: vale o pedido mais novo.
        if (pedido !== this._pedidoLista) return;
        const porId = new Map();
        listas.flat().filter(Boolean).forEach((p) => porId.set(p.id, p));
        this.pacientes = [...porId.values()].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "pt-BR"),
        );
      } catch (e) {
        if (!silencioso) this.pacientes = [];
      } finally {
        if (!silencioso) this.carregando = false;
      }
    },

    /** "Todas as clínicas": marca todas; se já estavam todas, volta à primeira. */
    alternarTodasClinicas() {
      this.clinicaIds = this.todasClinicas
        ? [this.clinicas[0].id]
        : this.clinicas.map((c) => c.id);
      this.carregarPacientes();
    },

    async carregarPacientesDoMedico({ silencioso = false } = {}) {
      try {
        const { data } = await http.get(
          `/api/relation/patients-by-doctor/${this.userId}`,
          { params: { visibility: "visible" } }, // SALVIA-955
        );
        this.pacientes = Array.isArray(data?.data) ? data.data : [];
      } catch (e) {
        if (!silencioso) this.pacientes = [];
      }
    },

    /**
     * Quem ja tem horario hoje. UMA chamada para a lista inteira, em vez de
     * uma por paciente — a agenda do dia ja vem pronta deste endpoint.
     */
    async carregarConsultasDeHoje() {
      try {
        const { data } = await http.get("/api/events/today", {
          params: { time_zone: userTimeZone() },
        });

        const mapa = {};

        // Só o que ainda vai acontecer: quem já foi atendido (ou faltou) não
        // é "consulta hoje" para quem está escolhendo o próximo. A lista vem
        // em ordem de horário, então vale a primeira pendente do paciente.
        const pendentes = ["scheduled", "confirmed", "waiting", "pending"];

        for (const evento of data?.events || []) {
          const id = evento.patient?.id;
          if (id && pendentes.includes(evento.status) && !mapa[id]) {
            mapa[id] = formatTime(evento.start);
          }
        }

        this.consultasDeHoje = mapa;
      } catch (e) {
        this.consultasDeHoje = {};
      }
    },

    temConsultaHoje(patientId) {
      return this.consultasDeHoje[patientId] || null;
    },

    /**
     * Quebra o nome em trechos para destacar o termo buscado. Sem `v-html`: o
     * nome vem do cadastro, e interpolado ele nunca vira marcacao.
     */
    partesDoNome(nome) {
      const texto = String(nome || "");
      const termo = (this.busca || "").trim();

      if (!termo) return [{ texto, destaque: false }];

      const i = texto.toLowerCase().indexOf(termo.toLowerCase());
      if (i < 0) return [{ texto, destaque: false }];

      return [
        { texto: texto.slice(0, i), destaque: false },
        { texto: texto.slice(i, i + termo.length), destaque: true },
        { texto: texto.slice(i + termo.length), destaque: false },
      ].filter((parte) => parte.texto);
    },

    iniciarPrimeiro() {
      if (this.ordenados.length) this.iniciar(this.ordenados[0]);
    },

    /** SALVIA-945: abre o cadastro rápido (o nome digitado na busca vem junto). */
    abrirCadastroRapido() {
      this.cadastroRapido = true;
    },

    /**
     * Voltou do cadastro rápido: recarrega a lista — o paciente novo (ou o
     * existente que acabou de ser vinculado) passa a fazer parte dela, pronto
     * para iniciar o atendimento aqui mesmo.
     */
    async aoCadastrarRapido({ patient } = {}) {
      await this.recarregarLista();
      this.destacar(patient?.id);
    },

    /** Recarrega a lista mantendo as clínicas marcadas. */
    async recarregarLista(opcoes = {}) {
      this.listaDesatualizada = false;
      if (this.userType === "doctor_solo" || !this.clinicas.length) {
        await this.carregarPacientesDoMedico(opcoes);
      } else {
        await this.carregarPacientes(opcoes);
      }
    },

    /** Borda do paciente pisca duas vezes (CSS na-paciente--novo) e rola até ele. */
    destacar(id) {
      if (!id) return;
      this.destacadoId = null;
      this.$nextTick(() => {
        this.destacadoId = id;
        this.$nextTick(() => {
          const el = this.$el.ownerDocument.querySelector(`[data-paciente-id="${id}"]`);
          el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
        clearTimeout(this._destaqueTimer);
        this._destaqueTimer = setTimeout(() => { this.destacadoId = null; }, 1800);
      });
    },

    iniciar(paciente) {
      this.aberto = false;

      this.$router.push({
        path: `/patient-profile-dashboard/${paciente.id}`,
        query: { atendimento: "novo" },
      });
    },
  },
};
</script>

<style scoped>
/* Nível 3 da coluna de atalhos. A ordem e o colapso são decididos pelo
   FloatingDock, que redefine as variáveis --ds-fab-* de fora; aqui ficam só os
   valores de quando o dock não existe. */
.na-launcher {
  position: fixed;
  right: var(--ds-fab-right, 24px);
  bottom: var(--ds-fab-bottom-3, 280px);
  z-index: var(--ds-fab-z, 95);
}
.na-fab {
  width: var(--ds-fab-h-0, 56px);
  height: var(--ds-fab-h-0, 56px);
  box-shadow: 0 6px 18px rgba(183, 28, 28, 0.28);
}

.na-card {
  border-radius: 14px !important;
  overflow: hidden;
  background: var(--ds-surface-white, #fff);
}

.na-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 22px;
  color: #fff;
  background:
    radial-gradient(circle at 92% -20%, rgba(255, 255, 255, 0.22) 0%, transparent 45%),
    linear-gradient(135deg, var(--ds-red, #f44336) 0%, #b71c1c 100%);
}
.na-header__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
}
.na-header__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.na-title {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}
.na-subtitle {
  font-size: 0.8rem;
  opacity: 0.85;
}

.na-toolbar {
  padding: 16px 22px 12px;
  border-bottom: 1px solid var(--ds-border-soft, #eef2f7);
}
.na-clinicas {
  margin-bottom: 10px !important; /* o .v-input do Vuetify zera a margem */
  border-radius: 10px;
  background: var(--ds-surface-soft, #f7f9fc);
  box-shadow: inset 0 0 0 1px var(--ds-border-soft, #eef2f7);
}
.na-clinicas :deep(.v-input__slot) {
  background: transparent !important;
  min-height: 38px !important;
}
.na-clinicas__resumo {
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  font-weight: 600;
  color: #b71c1c;
}
.na-clinicas__mais {
  margin-left: 6px;
  padding: 0 7px;
  border-radius: 999px;
  background: rgba(183, 28, 28, 0.1);
  color: #b71c1c;
  font-size: 0.72rem;
  font-weight: 700;
}
/* SALVIA-945: paciente recém-cadastrado — a borda pisca duas vezes. */
.na-paciente--novo {
  animation: na-novo-pulso 0.8s ease-in-out 2;
}
@keyframes na-novo-pulso {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(183, 28, 28, 0);
    border-color: inherit;
  }
  50% {
    box-shadow: 0 0 0 3px rgba(183, 28, 28, 0.55);
    border-color: #b71c1c;
  }
}
/* SALVIA-945: busca + botão de cadastro rápido na mesma linha. */
.na-busca-linha {
  display: flex;
  align-items: center;
  gap: 10px;
}
.na-busca-linha .na-busca {
  flex: 1 1 auto;
  min-width: 0;
}
.na-novo-paciente {
  flex: 0 0 auto;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}
@media (max-width: 600px) {
  /* Tela estreita: só o ícone, a busca não perde espaço. */
  .na-novo-paciente__texto {
    display: none;
  }
}
.na-busca {
  border-radius: 10px;
  background: var(--ds-surface-soft, #f7f9fc);
  box-shadow: inset 0 0 0 1px var(--ds-border-soft, #eef2f7);
  transition: box-shadow 0.15s ease;
}
.na-busca:focus-within {
  box-shadow: inset 0 0 0 2px rgba(183, 28, 28, 0.45);
}
.na-busca :deep(.v-input__slot) {
  background: transparent !important;
}
.na-contagem {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ds-text-soft, #78909c);
  white-space: nowrap;
  align-self: center;
}

/* Sem altura fixa: o diálogo `scrollable` limita a 90% da tela e rola só
   a lista — uma clínica com 300 pacientes não empurra o diálogo para fora.
   O min-height só evita o diálogo encolher e "pular" a cada letra da busca;
   a coluna flex deixa o estado vazio centralizado nessa altura. */
.na-corpo {
  min-height: min(58vh, 520px);
  display: flex;
  flex-direction: column;
}
/* As seções da lista NÃO encolhem: o .ds-form-section tem overflow: hidden,
   e numa coluna flex isso o deixava encolher até a altura visível e cortar os
   pacientes (ex.: "Todas as clínicas" com 60 pacientes mostrava 12, sem
   rolagem). Sem encolher, a lista cresce e quem rola é o corpo do diálogo. */
.na-corpo > .ds-form-section,
.na-corpo > .na-grade {
  flex-shrink: 0;
}

.na-grade {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.na-paciente {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  color: var(--ds-text, #2c3e50);
  background: var(--ds-surface-white, #fff);
  border: 1px solid var(--ds-border-soft, #eef2f7);
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease;
}
.na-paciente:hover,
.na-paciente:focus-visible {
  outline: none;
  transform: translateY(-2px);
  border-color: rgba(183, 28, 28, 0.35);
  box-shadow: 0 8px 20px rgba(183, 28, 28, 0.12);
}
.na-paciente--hoje {
  border-color: rgba(183, 28, 28, 0.25);
  background: linear-gradient(135deg, #fff5f5 0%, var(--ds-surface-white, #fff) 70%);
}
.na-paciente__avatar {
  flex-shrink: 0;
  box-shadow: 0 0 0 2px var(--ds-surface-white, #fff), 0 0 0 3px var(--ds-border-soft, #eef2f7);
}
.na-paciente--hoje .na-paciente__avatar {
  box-shadow: 0 0 0 2px var(--ds-surface-white, #fff), 0 0 0 3px rgba(183, 28, 28, 0.45);
}
.na-paciente__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.na-paciente__nome {
  font-size: 0.88rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.na-paciente__nome .na-destaque {
  padding: 0 1px;
  border-radius: 3px;
  color: inherit;
  background: rgba(232, 160, 75, 0.35);
}
.na-paciente__meta,
.na-paciente__hora {
  font-size: 0.74rem;
  color: var(--ds-text-soft, #78909c);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.na-paciente__hora {
  color: #b71c1c;
  font-weight: 600;
}
.na-paciente__hora .v-icon {
  color: inherit;
}
.na-paciente__seta {
  color: #b71c1c !important;
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.na-paciente--hoje .na-paciente__seta,
.na-paciente:hover .na-paciente__seta,
.na-paciente:focus-visible .na-paciente__seta {
  opacity: 1;
  transform: translateX(0);
}

/* Entrada/saída suave ao filtrar. */
.na-item-enter-active,
.na-item-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.na-item-enter,
.na-item-leave-to {
  opacity: 0;
  transform: scale(0.97);
}
.na-item-leave-active {
  position: absolute;
  visibility: hidden;
}
.na-item-move {
  transition: transform 0.18s ease;
}

.na-esqueleto {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--ds-border-soft, #eef2f7);
  border-radius: 12px;
}
.na-esqueleto__avatar,
.na-esqueleto__linhas span {
  background: linear-gradient(90deg, #eef2f7 25%, #f7f9fc 50%, #eef2f7 75%);
  background-size: 200% 100%;
  animation: na-brilho 1.2s ease-in-out infinite;
}
.na-esqueleto__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}
.na-esqueleto__linhas {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}
.na-esqueleto__linhas span {
  height: 10px;
  border-radius: 4px;
}
.na-esqueleto__linhas span:last-child {
  width: 60%;
}
@keyframes na-brilho {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

.na-vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  text-align: center;
  color: var(--ds-text-muted, rgba(0, 0, 0, 0.6));
  font-size: 0.88rem;
}
.na-vazio p {
  margin: 8px 0 0;
}
.na-vazio__icone {
  color: var(--ds-text-muted, #90a4ae) !important;
  opacity: 0.6;
}

.na-rodape {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 22px;
  font-size: 0.72rem;
  color: var(--ds-text-soft, #78909c);
  border-top: 1px solid var(--ds-border-soft, #eef2f7);
}

@media (max-width: 600px) {
  .na-grade {
    grid-template-columns: 1fr;
  }
  .na-rodape {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .na-paciente,
  .na-paciente__seta,
  .na-item-enter-active,
  .na-item-leave-active,
  .na-item-move {
    transition: none;
  }
  .na-esqueleto__avatar,
  .na-esqueleto__linhas span {
    animation: none;
  }
}
</style>
